// AutoPay (UPI AutoPay / card mandate / bank e-mandate) client helpers.
// The server sizes every mandate and every debit; nothing here is trusted
// for amounts.
import { api } from "./axios-instance";
import { auth, authStateKnown } from "../helper/firebaseClient";
import { loadRazorpay } from "./razorpay";

export const AUTOPAY_METHOD_COPY = {
  upi: {
    title: "UPI AutoPay",
    subtitle:
      "Google Pay, PhonePe, Paytm, BHIM, Pop UPI or any UPI app with AutoPay",
    auth: "Approve once in your UPI app with your UPI PIN (₹1, refunded).",
  },
  card: {
    title: "Credit / Debit card",
    subtitle: "Visa, Mastercard, RuPay — cards that support e-mandates",
    auth: "Verify once with OTP (₹1, refunded). Not every bank supports card mandates.",
  },
  emandate: {
    title: "Bank e-Mandate",
    subtitle: "Net banking or Debit card · best for yearly and large plans",
    auth: "Authorise on your bank's page. ₹0 is charged. Bank confirmation can take up to 2 working days.",
  },
};

async function authHeaders() {
  await authStateKnown;
  const user = auth.currentUser;
  if (!user) throw new Error("Please log in again.");
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

export function apiError(error, fallback) {
  const d = error?.response?.data;
  if (typeof d?.error === "string") return d.error;
  if (typeof d?.detail === "string") return d.detail;
  if (error?.message && !error?.response) return error.message;
  return fallback;
}

export async function fetchAutopayOptions({
  orgId,
  planCode,
  billingCycle,
  seats,
  isSeatTopup,
}) {
  const { data } = await api.post(
    "/payments/autopay/options/",
    {
      org_id: orgId,
      plan_code: planCode,
      billing_cycle: billingCycle,
      seats,
      is_seat_topup: Boolean(isSeatTopup),
    },
    { headers: await authHeaders() }
  );
  return data;
}

export async function fetchAutopayStatus(orgId) {
  const { data } = await api.get("/payments/autopay/", {
    params: { org_id: orgId },
    headers: await authHeaders(),
  });
  return data;
}

export async function cancelAutopay(orgId) {
  const { data } = await api.post(
    "/payments/autopay/cancel/",
    { org_id: orgId },
    { headers: await authHeaders() }
  );
  return data;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function pollMandate(
  orgId,
  mandateId,
  { tries = 6, delayMs = 4000 } = {}
) {
  for (let i = 0; i < tries; i += 1) {
    await sleep(delayMs);
    try {
      const { data } = await api.get(
        `/payments/autopay/mandate/${mandateId}/`,
        {
          params: { org_id: orgId },
          headers: await authHeaders(),
        }
      );
      if (
        data?.status &&
        data.status !== "pending_confirmation" &&
        data.status !== "created"
      ) {
        return data.status;
      }
    } catch (e) {
      // keep polling; the hourly worker also reconciles
    }
  }
  return "pending_confirmation";
}

/**
 * Registers a mandate end-to-end.
 * Resolves to { status: "active" | "pending_confirmation" | "rejected" | "cancelled_by_user" }.
 * Rejects with an Error (message is user-facing) on hard failures.
 */
export async function setupAutopayMandate({
  orgId,
  method,
  authType,
  bankAccount,
  theme = "#5E5ADB",
}) {
  const loaded = await loadRazorpay();
  if (!loaded || !window.Razorpay) {
    throw new Error(
      "Couldn't load the payment gateway. Check your connection and try again."
    );
  }

  let mandate;
  try {
    const { data } = await api.post(
      "/payments/autopay/mandate/",
      {
        org_id: orgId,
        method,
        auth_type: method === "emandate" ? authType : undefined,
        bank_account: method === "emandate" ? bankAccount : undefined,
      },
      { headers: await authHeaders() }
    );
    mandate = data;
  } catch (e) {
    throw new Error(apiError(e, "Couldn't start AutoPay setup."));
  }

  if (!mandate?.order_id || !mandate?.customer_id || !mandate?.key) {
    throw new Error("The server returned an invalid AutoPay request.");
  }

  const authResponse = await new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: mandate.key,
      order_id: mandate.order_id,
      customer_id: mandate.customer_id,
      recurring: "1",
      name: "TwoKey",
      description:
        method === "emandate"
          ? `AutoPay e-mandate (up to ₹${Number(mandate.max_amount_rupees).toLocaleString("en-IN")})`
          : `AutoPay up to ₹${Number(mandate.max_amount_rupees).toLocaleString("en-IN")} per renewal`,
      prefill: mandate.prefill || {},
      notes: { org_id: orgId, purpose: "autopay_mandate" },
      theme: { color: theme },
      modal: {
        ondismiss: () => resolve(null),
      },
      handler: (response) => resolve(response),
    });
    rzp.on("payment.failed", (response) => {
      reject(
        new Error(
          response?.error?.description || "AutoPay authorisation failed."
        )
      );
    });
    rzp.open();
  });

  if (!authResponse) return { status: "cancelled_by_user" };

  let status;
  try {
    const { data } = await api.post(
      "/payments/autopay/mandate/verify/",
      { org_id: orgId, ...authResponse },
      { headers: await authHeaders() }
    );
    status = data?.status;
  } catch (e) {
    if (e?.response?.status === 409 || e?.response?.status === 502) {
      status = "pending_confirmation";
    } else {
      throw new Error(apiError(e, "Couldn't confirm AutoPay."));
    }
  }

  if (status === "pending_confirmation" || status === "created") {
    status = await pollMandate(orgId, mandate.mandate_id);
  }
  return { status, mandateId: mandate.mandate_id };
}
